<template>
  <div>
    <!-- Breadcrumbs -->
    <v-breadcrumbs :items="items" class="px-0">
      <template v-slot:divider>
        <v-icon>mdi-chevron-right</v-icon>
      </template>
    </v-breadcrumbs>

    <v-row>
      <v-col cols="12">
        <!-- Alerts -->
        <v-alert
            v-if="error_alert"
            dense
            outlined
            type="error"
            icon="mdi-alert-circle-outline"
            class="mb-4 rounded-lg"
        >
          {{ error_alert }}
        </v-alert>

        <v-alert
            v-if="success_alert"
            dense
            outlined
            type="success"
            icon="mdi-check-circle-outline"
            class="mb-4 rounded-lg"
        >
          {{ success_alert }}
        </v-alert>
      </v-col>

      <v-col cols="12">
        <!-- Main Card -->
        <v-card class="elevation-1 rounded-lg">
          <v-card-title class="d-flex align-center py-4 px-5 card-header">
            <span class="font-weight-medium text-h5">Chamber Information</span>
            <v-spacer></v-spacer>

            <!-- Search -->
            <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search chambers..."
                single-line
                hide-details
                outlined
                dense
                class="mr-4 rounded-pill"
                style="max-width: 300px"
                color="primary"
            ></v-text-field>

            <!-- Print Button -->
            <v-btn
                depressed
                color="primary"
                class="text-capitalize rounded-pill px-4"
                @click="printList"
            >
              <v-icon left small>mdi mdi-printer</v-icon>
              Print
            </v-btn>


          </v-card-title>

          <v-divider></v-divider>

          <!-- Table -->
          <div id="print">
            <v-data-table
                :headers="headers"
                :items="ChamberInfos"
                :search="search"
                :loading="loadingStatus"
                loading-text="Loading chamber information..."
                class="elevation-0 custom-data-table"
                mobile-breakpoint="0"
                dense
            >
              <!-- Status Chip -->
              <template v-slot:item.status="{ item }">
                <v-chip
                    :color="item.status == 1 ? 'success' : 'warning'"
                    dark
                    small
                    class="px-3 py-1 rounded-pill font-weight-medium"
                    :outlined="item.status != 1"
                >
                  <v-icon x-small left v-if="item.status == 1">mdi-check</v-icon>
                  {{ item.status == 1 ? "Active" : "Inactive" }}
                </v-chip>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <v-btn
                    icon
                    small
                    color="primary"
                    @click="edit_detail(item)"
                    class="mx-1 action-btn"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>

              <!-- No Data -->
              <template v-slot:no-data>
                <div class="text-center py-4">
                  <v-icon color="grey lighten-1" large>mdi-database-remove-outline</v-icon>
                  <p class="grey--text text--lighten-1">No chamber data available</p>
                  <v-btn color="primary" class="mt-2 rounded-pill" @click="get_chamber_info()">
                    <v-icon left small>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </div>
              </template>

              <!-- Loading -->
              <template v-slot:loading>
                <v-row justify="center" align="center" class="py-4">
                  <v-progress-circular indeterminate color="primary" class="mr-2"></v-progress-circular>
                  <span>Loading chamber information...</span>
                </v-row>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit/Create Dialog -->
    <v-dialog v-model="method_item_dialog" max-width="700" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="text-h6 font-weight-bold primary white--text px-4 py-3">
          <v-icon left color="white">mdi-office-building-outline</v-icon>
          {{ formData.chamber_id ? 'Edit Chamber' : 'Create New Chamber' }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="method_item_dialog = false" class="ml-auto">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-form class="px-2">
          <v-container class="py-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                    v-model="formData.chamber_name"
                    outlined
                    dense
                    label="Chamber Name"
                    placeholder="Enter chamber name"
                    required
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                    v-model="formData.phone"
                    outlined
                    dense
                    label="Phone Number"
                    placeholder="Enter phone number"
                    required
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                    v-model="formData.email"
                    outlined
                    dense
                    label="Email Address"
                    placeholder="Enter email address"
                    required
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <label class="v-label theme--dark primary--text mb-2 d-block">Chamber Information</label>
                <vue-editor
                    v-model="formData.chamber_info"
                    class="rounded-lg editor-container"
                    placeholder="Enter chamber information details..."
                ></vue-editor>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                    v-model="formData.appointment_start"
                    outlined
                    dense
                    label="Appointment Start Time"
                    placeholder="HH:MM"
                    required
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                    v-model="formData.appointment_end"
                    outlined
                    dense
                    label="Appointment End Time"
                    placeholder="HH:MM"
                    required
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                    v-model="formData.appointment_slot"
                    label="Appointment Slot Duration"
                    outlined
                    dense
                    type="number"
                    placeholder="Minutes"
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                    v-model="formData.sms_notification"
                    :items="status"
                    item-value="key"
                    item-text="value"
                    label="SMS Notification"
                    outlined
                    dense
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                    v-model="formData.chamber_active_day"
                    label="Chamber Active Days"
                    outlined
                    dense
                    placeholder="e.g. Mon, Wed, Fri"
                    required
                    color="primary"
                    class="rounded-lg"
                    hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-divider></v-divider>

        <!-- Actions -->
        <v-card-actions class="px-4 py-3">
          <v-btn text @click="method_item_dialog = false" class="rounded-pill px-4">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="warning" text @click="formData = {}" class="rounded-pill px-4">
            <v-icon left small>mdi-autorenew</v-icon>
            Reset
          </v-btn>
          <v-btn color="primary" depressed @click="chamberInfoSubmit()" class="rounded-pill px-4">
            <v-icon left small>{{ formData.chamber_id ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
            {{ formData.chamber_id ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import atshq from "../../../js/setup";
import {VueEditor} from "vue2-editor";

export default {
  components: {
    VueEditor,
  },
  data() {
    return {
      search: "",
      auto_search: "",
      dialog: false,
      method_item_dialog: false,
      error_alert: null,
      success_alert: null,
      loadingStatus: true,
      status: [
        {key: 1, value: "Active"},
        {key: 0, value: "Inactive"},
      ],
      headers: [
        {
          text: "ID",
          align: "start",
          sortable: false,
          value: "chamber_id",
        },
        {text: "Name", value: "chamber_name", },
        {text: "Start", value: "appointment_start" },
        {text: "End", value: "appointment_end" },
        {text: "Slot", value: "appointment_slot"},
        {text: "Active Day", value: "chamber_active_day"},
        {text: "Phone", value: "phone"},
        {text: "Email", value: "email"},
        {text: "Notification", value: "sms_notification"},
        {text: "Create Date", value: "created_at"},
        {text: "Action", value: "actions", sortable: false, align: 'center'},
      ],
      desserts: [],
      formData: {
        chamber_id: null,
        chamber_name: null,
        phone: null,
        email: null,
        chamber_info: null,
        appointment_start: null,
        appointment_end: null,
        appointment_slot: null,
        chamber_active_day: null,
        sms_notification: null,
      },
      ChamberInfos: [],

      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Administrative",
          disabled: true,
          href: "patients",
        },
        {
          text: "Chamber Info",
          disabled: true,
          href: "patients",
        },
      ],

    };
  },

  created() {
    let storedGroups = localStorage.getItem("userGroups") || "";
    let userGroups = storedGroups
        .split(",")
        .map(g => Number(g.trim()))
        .filter(g => !isNaN(g));

    let permittedGroups = [1, 8]; // Admin=1, Manager=8
    let hasAccess = userGroups.some(group => permittedGroups.includes(group));

    if (hasAccess) {
      console.log("Access Granted",hasAccess);
    } else {
      this.loadingstatus = false;
      this.error_alert = "Invalid Request";
      return false;
    }

    this.get_chamber_info();
  },
  methods: {
    get_chamber_info() {
      atshq
          .get("administrative/chamber-info")
          .then((response) => {
            this.ChamberInfos = response.data.ChamberInfos;
            this.loadingStatus = false;
          })
          .catch((e) => {
            this.error_alert.push(e);
          });
    },
    edit_detail(item) {
      this.method_item_dialog = true;
      this.formData.chamber_id = item.chamber_id;
      this.formData.chamber_name = item.chamber_name;
      this.formData.phone = item.phone;
      this.formData.email = item.email;
      this.formData.chamber_info = item.chamber_info;
      this.formData.appointment_start = item.appointment_start;
      this.formData.appointment_end = item.appointment_end;
      this.formData.appointment_slot = item.appointment_slot;
      this.formData.chamber_active_day = item.chamber_active_day;
      this.formData.sms_notification = item.sms_notification;
    },
    chamberInfoSubmit() {
      if (this.formData.chamber_id) {
        atshq
            .put("administrative/chamber-info/" + this.formData.chamber_id, {
              formData: this.formData,
              user_id: localStorage.getItem("userId"),
            })
            .then((response) => {
              this.$noty.success(response.data.message);
              this.get_chamber_info();
              this.method_item_dialog = false;
            })
            .catch((e) => {
              this.$noty.error(e.response.data.message);
            })

      } else {

        atshq
            .post("administrative/chamber-info", {
              formData: this.formData,
              user_id: localStorage.getItem("userId"),
            })
            .then((response) => {
              this.$noty.success(response.data.message);
              this.get_chamber_info();
              this.method_item_dialog = false;
            })
            .catch((e) => {
              this.$noty.error(e.response.data.message);
            })

      }
    },
    printList() {
      var disp_setting = "toolbar=yes,location=no,";
      disp_setting += "directories=yes,menubar=yes,";
      disp_setting += "scrollbars=yes,width=650, height=600, left=100, top=25";
      var content_vlue = document.getElementById("print").innerHTML;
      var docprint = window.open("", "", disp_setting);
      docprint.document.open();
      docprint.document.write('<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN"');
      docprint.document.write('"http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">');
      docprint.document.write(
          '<html xmlns="http://www.w3.org/1999/xhtml" xml:lang="en">'
      );

      docprint.document.write('<style type="text/css">body{ margin:0px;');
      docprint.document.write("font-family:verdana,Arial;color:#000;");

      docprint.document.write(
          "font-family:Verdana, Geneva, sans-serif; font-size:10px;}"
      );
      docprint.document.write(
          "table td, table th {border: 1px solid #ddd;padding: 0px; font-size:9px;}"
      );
      docprint.document.write(
          "table th, table th {padding-top: 12px;padding-bottom: 12px;text-align: left;background-color: #4CAF50;color: white;}"
      );
      docprint.document.write("table {width: 100%;}");

      docprint.document.write("a{color:#000;text-decoration:none;} </style>");
      docprint.document.write('</head><body onLoad="self.print()"><center>');
      docprint.document.write("<h3> Chmaber Info  || " + this.date + "</h3>");
      docprint.document.write(content_vlue);
      docprint.document.write("</center></body></html>");
      docprint.document.close();
      docprint.focus();
    },
  },
};
</script>

<style scoped>
.card-header {
  background-color: #f7f9fc;
  border-bottom: 1px solid #e0e0e0;
}

.custom-data-table {
  border: none;
}

.custom-data-table ::v-deep th {
  background-color: #f5f7fa;
  color: #344767;
  font-weight: 600;
  height: 48px;
}

.custom-data-table ::v-deep tr:nth-of-type(even) {
  background-color: #f9fafc;
}

.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.editor-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}
</style>